import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { equipmentLifecycleService } from '../../../lib/equipmentLifecycleService';

const DepreciationTracker = ({ equipment, onRefresh }) => {
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [depreciationData, setDepreciationData] = useState([]);
  const [loading, setLoading] = useState(false);

  const equipmentOptions = equipment?.map((eq) => ({
    value: eq?.id,
    label: `${eq?.name} - ${eq?.serial_number}`
  }));

  useEffect(() => {
    if (selectedEquipment) {
      loadDepreciationData();
    }
  }, [selectedEquipment]);

  const loadDepreciationData = async () => {
    setLoading(true);
    try {
      const { data } = await equipmentLifecycleService?.getDepreciation(selectedEquipment);
      if (data) setDepreciationData(data);
    } catch (error) {
      console.error('Error loading depreciation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalDepreciation = () => {
    return equipment?.reduce((total, eq) => {
      return total + (parseFloat(eq?.purchase_cost) || 0);
    }, 0);
  };

  const calculateAverageAge = () => {
    const totalAge = equipment?.reduce((sum, eq) => {
      const age = new Date()?.getFullYear() - new Date(eq?.purchase_date)?.getFullYear();
      return sum + age;
    }, 0);
    return (totalAge / equipment?.length)?.toFixed(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })?.format(amount);
  };

  const getDepreciationMethodLabel = (method) => {
    const labels = {
      straight_line: 'Straight Line',
      declining_balance: 'Declining Balance',
      sum_of_years_digits: 'Sum of Years Digits',
      units_of_production: 'Units of Production'
    };
    return labels?.[method] || method;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="DollarSign" size={20} className="text-primary" />
            <h3 className="text-sm font-medium text-muted-foreground">Total Asset Value</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{formatCurrency(calculateTotalDepreciation())}</p>
          <p className="text-xs text-muted-foreground mt-1">Original purchase cost</p>
        </div>

        <div className="bg-card rounded-xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="TrendingDown" size={20} className="text-warning" />
            <h3 className="text-sm font-medium text-muted-foreground">Average Equipment Age</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{calculateAverageAge()}</p>
          <p className="text-xs text-muted-foreground mt-1">Years since purchase</p>
        </div>

        <div className="bg-card rounded-xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Package" size={20} className="text-success" />
            <h3 className="text-sm font-medium text-muted-foreground">Total Equipment</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{equipment?.length || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Active assets</p>
        </div>
      </div>
      {/* Equipment Depreciation Table */}
      <div className="bg-card rounded-xl border border-border">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="TrendingDown" size={20} />
            Equipment Depreciation Overview
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Equipment</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Purchase Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Purchase Cost</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Age (Years)</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Useful Life</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Remaining Life</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase">Depreciation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {equipment?.map((eq) => {
                const age = new Date()?.getFullYear() - new Date(eq?.purchase_date)?.getFullYear();
                const depreciationRate = age / (eq?.estimated_useful_life_years || 10);
                const depreciationPercent = Math.min(depreciationRate * 100, 100);

                return (
                  <tr key={eq?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-foreground">{eq?.name}</p>
                        <p className="text-xs text-muted-foreground">{eq?.serial_number}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {new Date(eq?.purchase_date)?.toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      {formatCurrency(eq?.purchase_cost)}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{age}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{eq?.estimated_useful_life_years}</td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {eq?.remaining_useful_life_years?.toFixed(1)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-warning transition-all"
                            style={{ width: `${depreciationPercent}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-foreground w-12 text-right">
                          {depreciationPercent?.toFixed(0)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Detailed Depreciation View */}
      <div className="bg-card rounded-xl border border-border">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">Detailed Depreciation Analysis</h3>
          <Select
            options={equipmentOptions}
            value={selectedEquipment}
            onChange={setSelectedEquipment}
            placeholder="Select equipment to view depreciation details"
          />
        </div>
        {selectedEquipment && (
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : depreciationData?.length > 0 ? (
              <div className="space-y-4">
                {depreciationData?.map((dep) => (
                  <div key={dep?.id} className="bg-muted/30 rounded-lg p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Method</p>
                        <p className="text-sm font-medium text-foreground">
                          {getDepreciationMethodLabel(dep?.depreciation_method)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Current Book Value</p>
                        <p className="text-sm font-medium text-foreground">
                          {formatCurrency(dep?.current_book_value)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Accumulated Depreciation</p>
                        <p className="text-sm font-medium text-foreground">
                          {formatCurrency(dep?.accumulated_depreciation)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Fiscal Year</p>
                        <p className="text-sm font-medium text-foreground">{dep?.fiscal_year}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No depreciation records found</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DepreciationTracker;